import React, { FC } from "react";
import { Box, MenuItem, Select, TextField, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { HAMASPIK_COLORS, HAMASPIK_CONTROL_HEIGHT, HAMASPIK_FONT_DISPLAY } from "../../../../../assets/hamaspik/theme";
import Badge from "../../../../../assets/hamaspik/Badge";
import { PostIfLookupItemDto, PostIfPositionRulesDto } from "../../../../../types/pifDto";
import { formatPifDate, parsePifDate } from "../../pifDates";
import { pifFieldId } from "../../pifFocus";
import { isCommunityDsp, isFullTimeAllowed } from "../../pifValidation";
import { PifFormValues, toDecimalInput, toSelectId } from "../../types";
import PifField from "./PifField";
import PifSectionCard from "./PifSectionCard";

interface PifSectionPositionProps {
  values: PifFormValues;
  onChange: (patch: Partial<PifFormValues>) => void;
  rules: PostIfPositionRulesDto | null;
  departments: PostIfLookupItemDto[];
  positions: PostIfLookupItemDto[];
  reportToUsers: PostIfLookupItemDto[];
  services: PostIfLookupItemDto[];
  clients: PostIfLookupItemDto[];
}

const HIRE_TYPES = ["New employee", "Additional position (keeps current one)", "Position change (replaces current one)"];

const PifSectionPosition: FC<PifSectionPositionProps> = ({
  values: f,
  onChange,
  rules,
  departments,
  positions,
  reportToUsers,
  services,
  clients,
}) => {
  /** Department gates position, so changing it clears everything scoped beneath it. */
  const handleDeptChange = (id: number | null) => {
    onChange({
      deptId: id,
      dept: departments.find((d) => d.id === id)?.text ?? "",
      posId: null,
      pos: "",
      serviceId: null,
      service: "",
      clientId: null,
      client: "",
      reportsToId: null,
      reportsTo: "",
    });
  };

  const handlePosChange = (id: number | null) => {
    onChange({
      posId: id,
      pos: positions.find((p) => p.id === id)?.text ?? "",
      serviceId: null,
      service: "",
      clientId: null,
      client: "",
    });
  };

  const startDateValue = parsePifDate(f.startDate);
  const startPast = startDateValue ? startDateValue < new Date(new Date().toDateString()) : false;
  const hoursNum = parseFloat(f.hours);
  const hoursWarn = !isNaN(hoursNum) && ((f.empType === "Part-time" && hoursNum > 30) || hoursNum > 60);
  // Locked only once the rules are LOADED and actually forbid full-time. `rules === null` means no
  // position has been chosen yet, i.e. unknown — disabling on that locked the toggle with no
  // explanation (the note below has always been guarded on `rules`), and once the employment-history
  // seed started pre-selecting Full-time it rendered that selection in MUI's grey disabled palette,
  // which reads as "nothing is selected". PifWorkspace clamps the value if the position rules out FT.
  const ftLocked = !!rules && !isFullTimeAllowed(rules);
  // Service must be visible whenever it is required, or the missing-answer gap is unfillable:
  // requirement is isCommunityDsp(rules) while the services list loads independently, so gating
  // purely on services.length hid a required field during that window. Non-DSP positions keep
  // the optional dropdown when their department has services.
  const showService = isCommunityDsp(rules) || (!!rules && services.length > 0);
  const showClient = isCommunityDsp(rules);

  return (
    <PifSectionCard
      id="sec-position"
      title="The position"
      badge={rules?.classification ? <Badge variant="primary" size="sm">{rules.classification}</Badge> : undefined}
    >
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 1.75 }}>
        {/* SelectDisplayProps.id, not id: MUI puts `id` on the Select's hidden input, and the
            element a validation error has to focus is the display button. The plain `id` prop is
            right for every TextField / DatePicker below. See pifFocus. */}
        <PifField label="Kind of hire *">
          <Select
            size="small"
            fullWidth
            displayEmpty
            value={f.hireType}
            onChange={(e) => onChange({ hireType: e.target.value })}
            SelectDisplayProps={{ id: pifFieldId("hireType") }}
            data-testid="postif-kind-of-hire"
          >
            <MenuItem value="">Choose one…</MenuItem>
            {HIRE_TYPES.map((t) => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ))}
          </Select>
        </PifField>
        <PifField label="Department *">
          <Select
            size="small"
            fullWidth
            displayEmpty
            value={f.deptId ?? ""}
            onChange={(e) => handleDeptChange(toSelectId(e.target.value))}
            SelectDisplayProps={{ id: pifFieldId("deptId") }}
          >
            <MenuItem value="">Choose a department…</MenuItem>
            {departments.map((d) => (
              <MenuItem key={d.id} value={d.id}>
                {d.text}
              </MenuItem>
            ))}
          </Select>
        </PifField>
        <PifField label="Position *">
          <Select
            size="small"
            fullWidth
            displayEmpty
            disabled={!f.deptId}
            value={f.posId ?? ""}
            onChange={(e) => handlePosChange(toSelectId(e.target.value))}
            SelectDisplayProps={{ id: pifFieldId("posId") }}
          >
            <MenuItem value="">{f.deptId ? "Choose a position…" : "Pick a department first"}</MenuItem>
            {positions.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.text}
              </MenuItem>
            ))}
          </Select>
        </PifField>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 1.75 }}>
        <PifField label="Reports to *">
          <Select
            size="small"
            fullWidth
            displayEmpty
            disabled={!f.deptId}
            value={f.reportsToId ?? ""}
            onChange={(e) => {
              const id = toSelectId(e.target.value);
              onChange({ reportsToId: id, reportsTo: reportToUsers.find((u) => u.id === id)?.text ?? "" });
            }}
            SelectDisplayProps={{ id: pifFieldId("reportsToId") }}
          >
            <MenuItem value="">
              {!f.deptId
                ? "Pick a department first"
                : reportToUsers.length
                  ? "Choose a supervisor…"
                  : "No eligible supervisors for this department contact HR"}
            </MenuItem>
            {/* Same as Service below: a restored UserId that is not in this department's list has
                no MenuItem, so the Select would render blank and read as unsaved.
                The option COUNT is the diagnostic, so it goes early in the short label - MUI
                truncates the closed Select. A count of 0 means the department-scoped lookup
                returned nothing (its main-DB sproc fails closed when System_Settings_New has no
                'ActiveStatuses'); any other count means the stored id is stale for this
                department. */}
            {f.reportsToId !== null && !reportToUsers.some((u) => u.id === f.reportsToId) && (
              <MenuItem value={f.reportsToId} disabled>
                {`id ${f.reportsToId} — ${reportToUsers.length} options — re-pick`}
              </MenuItem>
            )}
            {reportToUsers.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.text}
              </MenuItem>
            ))}
          </Select>
        </PifField>
        <PifField label="Intended start date *">
          <DatePicker
            value={startDateValue}
            onChange={(value) => onChange({ startDate: formatPifDate(value) })}
            slotProps={{
              textField: { id: pifFieldId("startDate"), size: "small", fullWidth: true, inputProps: { readOnly: true } },
            }}
          />
          {startPast && (
            <Typography variant="caption" color="warning.main">
              That date has already passed allowed, but say why in the notes.
            </Typography>
          )}
        </PifField>
        <PifField label="Post-interview date">
          <TextField size="small" fullWidth disabled value={new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })} />
        </PifField>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" }, gap: 1.75, alignItems: "start" }}>
        <PifField label="Employment type *">
          <FtPtToggle id={pifFieldId("empType")} empType={f.empType} disabled={ftLocked} onChange={(v) => onChange({ empType: v })} />
          {ftLocked && (
            <Typography variant="caption" color="text.secondary">
              {isCommunityDsp(rules)
                ? "This position is always part-time."
                : "Not schedule-based, so it can't be classified full-time."}
            </Typography>
          )}
        </PifField>
        <PifField label="Hours per week *">
          <TextField id={pifFieldId("hours")} size="small" fullWidth value={f.hours} inputMode="decimal" placeholder="e.g. 35.5" onChange={(e) => onChange({ hours: toDecimalInput(e.target.value) })} />
          {hoursWarn && (
            <Typography variant="caption" color="warning.main">
              {f.empType === "Part-time" ? "Over 30 hours on a part-time line HR will query this." : "That's a lot of hours. Double-check."}
            </Typography>
          )}
        </PifField>
        {showService && (
          <PifField label={`Service ${isCommunityDsp(rules) ? "*" : ""}`}>
            <Select
              size="small"
              fullWidth
              displayEmpty
              value={f.serviceId ?? ""}
              onChange={(e) => {
                const id = toSelectId(e.target.value);
                onChange({
                  serviceId: id,
                  service: services.find((s) => s.id === id)?.text ?? "",
                  clientId: null,
                  client: "",
                });
              }}
              SelectDisplayProps={{ id: pifFieldId("serviceId") }}
            >
              <MenuItem value="">
                {services.length ? "Choose a service…" : "No services set up for this department"}
              </MenuItem>
              {/* A reopened draft can hold a ServiceId this department no longer offers. Without
                  a matching MenuItem, MUI renders the Select BLANK - indistinguishable from "the
                  value never saved" - so surface it and make the re-pick explicit. */}
              {f.serviceId !== null && !services.some((s) => s.id === f.serviceId) && (
                <MenuItem value={f.serviceId} disabled>
                  {`id ${f.serviceId} — ${services.length} options — re-pick`}
                </MenuItem>
              )}
              {services.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.text}
                </MenuItem>
              ))}
            </Select>
          </PifField>
        )}
      </Box>

      {showClient && (
        <Box sx={{ p: 2.125, bgcolor: "rgba(245,127,67,0.06)", border: "1.5px solid", borderColor: "warning.light", borderRadius: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Typography sx={{ fontFamily: HAMASPIK_FONT_DISPLAY, fontWeight: 800, fontSize: 13.5, color: HAMASPIK_COLORS.heading }}>Client assignment</Typography>
            <Badge variant="primary" size="sm" sx={{ bgcolor: "#fff" }}>COMMUNITY-BASED DSP</Badge>
          </Box>
          <PifField label="Individual / client *">
            <Select
              size="small"
              fullWidth
              displayEmpty
              disabled={!f.serviceId}
              value={f.clientId ?? ""}
              onChange={(e) => {
                const id = toSelectId(e.target.value);
                onChange({ clientId: id, client: clients.find((c) => c.id === id)?.text ?? "" });
              }}
              SelectDisplayProps={{ id: pifFieldId("clientId") }}
            >
              <MenuItem value="">{f.serviceId ? "Choose an individual…" : "Pick a service first"}</MenuItem>
              {clients.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.text}
                </MenuItem>
              ))}
            </Select>
          </PifField>
        </Box>
      )}

    </PifSectionCard>
  );
};

const FtPtToggle: FC<{
  /** Focus target for a validation error, on the first of the pair — see pifFocus. */
  id: string;
  empType: "Part-time" | "Full-time";
  disabled: boolean;
  onChange: (v: "Part-time" | "Full-time") => void;
}> = ({ id, empType, disabled, onChange }) => {
  return (
    <Box sx={{ display: "flex", gap: 1.25, "& .MuiButton-root": { minHeight: HAMASPIK_CONTROL_HEIGHT } }}>
      <Button id={id} variant={empType === "Part-time" ? "contained" : "outlined"} size="small" onClick={() => onChange("Part-time")}>
        Part-time
      </Button>
      {/* Primary on both halves: selecting Full-time used to switch this button to `secondary`,
          the burgundy reserved for app chrome, so the same control had two different "selected"
          colours. Rarely seen while Full-time could never be selected — obvious now that the
          employment-history seed pre-selects it. */}
      <Button
        variant={empType === "Full-time" ? "contained" : "outlined"}
        size="small"
        disabled={disabled}
        onClick={() => onChange("Full-time")}
      >
        Full-time
      </Button>
    </Box>
  );
};

export default PifSectionPosition;
